import { useTranslation } from "react-i18next"
import PageLayout from "components/layout/PageLayout"
import { useDeleteBusinessUnitMutation, useGetBusinessUnitsQuery } from "./api/businessUnitApiSlice"
import { NavLink } from "react-router-dom"
import { MapContainer, Marker, Popup, TileLayer, Tooltip } from "react-leaflet"
import * as L from "leaflet"
import { useState } from "react"
import BusinessUnitCard from "./views/BusinessUnitCard"
import HasPermission, { CanManageBusinessUnit } from "app/PermissionChecker"
import NoData from "components/commons/NoData"


function BusinessUnits() {

    const [t] = useTranslation("global")
    const [map, setMap] = useState(null)

    const canManageBusinessUnit = HasPermission(CanManageBusinessUnit)

    // API
    const {
        data: businessUnits,
        isFetching,
        isLoading,
        isSuccess,
        isError,
        error,
        refetch,
    } = useGetBusinessUnitsQuery()

    // API
    const [deleteBusinessUnit, deleteBusinessUnitResult] = useDeleteBusinessUnitMutation()

    var markerSize = { 'sm': [28, 35], 'md': [35, 44], 'lg': [42, 53] };
    var markerAnchor = { 'sm': [14, 35], 'md': [17, 44], 'lg': [21, 53] };
    var markerPopupAnchor = { 'sm': [1, -35], 'md': [1, -44], 'lg': [2, -53] };

    function getMarkerIcon(location) {
        return new L.Icon({
            iconUrl: `https://assets.mapquestapi.com/icon/v2/marker-lg-2c363c-2bb673-${location.name}.png`,
            iconSize: markerSize.lg,
            iconAnchor: markerAnchor.lg,
            popupAnchor: markerPopupAnchor.lg
        })
    }

    function focusMapOnMarker(businessUnitInfo) {
        if (businessUnitInfo.businessUnit.gps != null)
            map.setView([businessUnitInfo.businessUnit.gps.latitude, businessUnitInfo.businessUnit.gps.longitude], 15)
    }

    let content

    if (isSuccess) {

        const isBusinessUnitsListEmpty = () => businessUnits.length == 0

        const anyBusinessUnitHasGPS = () => {
            return businessUnits.some(businessUnitInfo => businessUnitInfo.businessUnit.gps != null);
        };


        const startLatitude = businessUnits.filter(businessUnitInfo => (businessUnitInfo.businessUnit.gps != null))[0]?.businessUnit.gps.latitude
        const startLongituted = businessUnits.filter(businessUnitInfo => (businessUnitInfo.businessUnit.gps != null))[0]?.businessUnit.gps.longitude


        const mapBounds = [
            [Math.max(...businessUnits.filter(bu => (bu.businessUnit.gps != null)).map(bu => bu.businessUnit.gps.latitude)), Math.min(...businessUnits.filter(bu => (bu.businessUnit.gps != null)).map(bu => bu.businessUnit.gps.longitude))],
            [Math.min(...businessUnits.filter(bu => (bu.businessUnit.gps != null)).map(bu => bu.businessUnit.gps.latitude)), Math.max(...businessUnits.filter(bu => (bu.businessUnit.gps != null)).map(bu => bu.businessUnit.gps.longitude))]
        ]

        content = <>
            {
                canManageBusinessUnit &&
                <div className="row mb-4">
                    <div className="col text-end">
                        <NavLink to={"/dashboard/businessUnits/new"} className="btn btn-primary"><i className="fa-solid fa-circle-plus"></i>  {t("action.add")}</NavLink>

                    </div>
                </div>

            }

            {isBusinessUnitsListEmpty() && <NoData/>}
            <div className="row row-cols-1 row-cols-sm-2 row-cols-md-4  row-cols-xl-5">
                {businessUnits.map(businessUnitInfo => (
                    <BusinessUnitCard
                        key={businessUnitInfo.businessUnit.guid}
                        onlyRead={!canManageBusinessUnit}
                        businessUnitInfo={businessUnitInfo}
                        onFocus={focusMapOnMarker}
                        onDelete={(guid) => deleteBusinessUnit(guid)}
                    />

                ))}
            </div>
            {
                anyBusinessUnitHasGPS() &&
                <div className="mt-2">
                    <MapContainer
                        bounds={mapBounds}
                        boundsOptions={{ padding: [100, 100] }}
                        ref={setMap}
                        scrollWheelZoom={true}>
                        <TileLayer
                            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        />
                        {businessUnits.filter(businessUnitInfo => (businessUnitInfo.businessUnit.gps != null)).map(bu => (
                            <Marker position={[bu.businessUnit.gps.latitude, bu.businessUnit.gps.longitude]} key={bu.businessUnit.guid} icon={getMarkerIcon(bu.businessUnit)} >
                                <Tooltip><b>{bu.businessUnit.name}</b></Tooltip>
                                <Popup>
                                    <small className="text-muted">{[bu.businessUnit.code]}</small>
                                    <h4>{[bu.businessUnit.name]}</h4>
                                    <p className="">
                                        <i className="fas fa-users"></i> {bu.employeesCount}
                                    </p>
                                    <NavLink to={"/dashboard/businessUnits/detail"} state={bu.businessUnit} className="btn-primary btn text-white"><i className="fa-solid fa-eye" ></i> {t("action.see")}</NavLink>
                                </Popup>
                            </Marker>
                        ))}
                    </MapContainer>

                </div>
            }

        </>
    }


    return <PageLayout
        pageTitle={t("entity.businessUnits")}
        bodyContent={content}
        onPageRefresh={refetch}
        isError={isError}
        errorMsg={error}
        isLoadingPage={isLoading}
        isLoadingContent={isFetching}
    />


}

export default BusinessUnits